import { z } from 'zod';

import { InclusionRoleSchema } from './person.js';
import { IsoDateSchema, IsoDateTimeSchema, NonEmptyStringSchema, QidSchema } from './primitives.js';

export const DailyDiscoveryPersonSchema = z.object({
  id: QidSchema,
  name: NonEmptyStringSchema,
  inclusionRole: InclusionRoleSchema,
  birthYear: z.number().int().optional(),
  domains: z.array(NonEmptyStringSchema),
});

export type DailyDiscoveryPerson = z.infer<typeof DailyDiscoveryPersonSchema>;

export const DailyDiscoveryTraceSchema = z.object({
  id: NonEmptyStringSchema,
  source: QidSchema,
  target: QidSchema,
  people: z.array(QidSchema).min(3).max(6),
  steps: z.number().int().min(2).max(5),
});

export type DailyDiscoveryTrace = z.infer<typeof DailyDiscoveryTraceSchema>;

export const DailyDiscoveryCollectionIdSchema = z.enum([
  'across-domains',
  'across-eras',
  'quick-surprises',
  'deeper-paths',
  'accepted-featured',
]);

export const DailyDiscoveryCollectionSchema = z.object({
  id: DailyDiscoveryCollectionIdSchema,
  title: NonEmptyStringSchema,
  description: NonEmptyStringSchema,
  traceIds: z.array(NonEmptyStringSchema).min(1).max(8),
});

export type DailyDiscoveryCollection = z.infer<typeof DailyDiscoveryCollectionSchema>;

export const DailyDiscoveryPayloadSchema = z
  .object({
    schemaVersion: z.literal(1),
    datasetVersion: NonEmptyStringSchema,
    generatedAt: IsoDateTimeSchema,
    snapshotDate: IsoDateSchema,
    configVersion: z.number().int().positive(),
    policyVersion: z.number().int().positive(),
    layoutVersion: z.number().int().positive(),
    people: z.array(DailyDiscoveryPersonSchema).min(1),
    traces: z.array(DailyDiscoveryTraceSchema).min(1).max(24),
    collections: z.array(DailyDiscoveryCollectionSchema).min(1).max(4),
  })
  .superRefine((value, context) => {
    const peopleById = new Map(value.people.map((person) => [person.id, person]));
    if (peopleById.size !== value.people.length) {
      context.addIssue({
        code: 'custom',
        path: ['people'],
        message: 'Daily discovery people must be unique.',
      });
    }
    const traceById = new Map(value.traces.map((trace) => [trace.id, trace]));
    if (traceById.size !== value.traces.length) {
      context.addIssue({
        code: 'custom',
        path: ['traces'],
        message: 'Daily discovery traces must be unique.',
      });
    }
    const endpoints = new Set<string>();
    for (const [index, trace] of value.traces.entries()) {
      if (
        trace.people[0] !== trace.source ||
        trace.people.at(-1) !== trace.target ||
        trace.people.length !== trace.steps + 1 ||
        new Set(trace.people).size !== trace.people.length ||
        trace.people.some((personId) => !peopleById.has(personId))
      ) {
        context.addIssue({
          code: 'custom',
          path: ['traces', index],
          message: 'Daily discovery trace paths must be complete, loop-free, and resolvable.',
        });
      }
      if (
        peopleById.get(trace.source)?.inclusionRole === 'context' ||
        peopleById.get(trace.target)?.inclusionRole === 'context'
      ) {
        context.addIssue({
          code: 'custom',
          path: ['traces', index],
          message: 'Daily discovery endpoints must remain first-class people.',
        });
      }
      if (endpoints.has(trace.source) || endpoints.has(trace.target)) {
        context.addIssue({
          code: 'custom',
          path: ['traces', index],
          message: 'Daily discovery endpoints must not repeat across the portfolio.',
        });
      }
      endpoints.add(trace.source);
      endpoints.add(trace.target);
    }
    const collectionIds = new Set<string>();
    for (const [index, collection] of value.collections.entries()) {
      if (
        collectionIds.has(collection.id) ||
        new Set(collection.traceIds).size !== collection.traceIds.length ||
        collection.traceIds.some((traceId) => !traceById.has(traceId))
      ) {
        context.addIssue({
          code: 'custom',
          path: ['collections', index],
          message: 'Daily discovery collections must be unique and reference valid traces.',
        });
      }
      collectionIds.add(collection.id);
    }
  });

export type DailyDiscoveryPayload = z.infer<typeof DailyDiscoveryPayloadSchema>;
